import React from 'react'
import { useRouter } from 'next/router'

import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon-v2'
import { useToast } from '@wartek-id/toast'

import { PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { DialogPelaksanaanAppealRollback } from 'app/KinerjaPSPegawaiPage/components/DialogPelaksaanaanAppeal/DialogPelaksanaanAppealRollback'
import { DialogWarningPelaksanaanAppealRollback } from 'app/KinerjaPSPegawaiPage/components/DialogPelaksaanaanAppeal/DialogWarningPelaksanaanAppealRollback'
import { DialogPelaksanaanAppealRequest } from 'app/KinerjaPSPegawaiPage/components/DialogPelaksaanaanAppeal/DialogPelaksanaanAppealRequest'
import { DialogPelaksanaanAppealSubmitted } from 'app/KinerjaPSPegawaiPage/components/DialogPelaksaanaanAppeal/DialogPelaksanaanAppealSubmitted'
import { useAPIPostPraktikKinerjaRollbackState } from 'api/kinerja/pengawas-sekolah/pelaksanaan/form'
import { usePelaksanaanPraktikKinerjaPS } from 'app/KinerjaPSPegawaiPage/hooks/usePelaksanaanPraktikKinerjaPS'
import { PELAKSANAAN_PRAKTIK_KINERJA_STATE_TITLE } from 'app/KinerjaPSPegawaiPage/constants/praktikKinerja'

import type { TPraktikKinerjaFormName } from 'types/kinerja-ps-pegawai'

const MSG_ERROR_SUBMIT = 'Gagal ubah dokumen menjadi draf. Coba lagi.'
const MSG_SUCCESS_REQUEST = 'Berhasil mengajukan perubahan!'

interface TButtonPelaksanaanAppealRollbackProps {
  formName: TPraktikKinerjaFormName
}
export const ButtonPelaksanaanAppealRollback = ({
  formName,
}: TButtonPelaksanaanAppealRollbackProps) => {
  const router = useRouter()
  const toast = useToast()

  const { psSummaryHookResult, isNeedRollbackApproval } =
    usePelaksanaanPraktikKinerjaPS({
      formName,
    })
  const { summary } = psSummaryHookResult
  const { mutateAsync } = useAPIPostPraktikKinerjaRollbackState({
    skpPsId: summary?.skpPsId ?? '',
    formName,
  })

  const [isDialogAppealRollbackOpen, setDialogAppealRollbackOpen] =
    React.useState<boolean>(false)
  const [
    isWarningDialogAppealRollbackOpen,
    setWarningDialogAppealRollbackOpen,
  ] = React.useState<boolean>(false)
  const [isDialogAppealSubmittedOpen, setDialogAppealSubmittedOpen] =
    React.useState<boolean>(false)

  const handleRollback = async () => {
    try {
      await mutateAsync({})

      if (isNeedRollbackApproval) {
        setDialogAppealSubmittedOpen(true)
      } else {
        router.reload()
      }
    } catch (err) {
      toast({ message: MSG_ERROR_SUBMIT })
    } finally {
      setDialogAppealRollbackOpen(false)
    }
  }

  const isAppealStateExist = React.useMemo(() => {
    return Boolean(summary?.pelaksanaanStatuses?.praktikKinerjaPerubahanStatus)
  }, [summary])

  const isPenetapanSentToEkin = React.useMemo(() => {
    return [
      'NOT_SENT',
      'SENT_TO_PPK',
      'SENDING_TO_BKN',
      'SENT_TO_BKN',
      'ERROR_SEND',
    ].includes(summary?.penetapanStatus)
  }, [summary?.penetapanStatus])

  const isDisabled = isAppealStateExist || isPenetapanSentToEkin

  return (
    <>
      <Button
        color={isDisabled ? 'black' : 'white'}
        className="flex items-center justify-center gap-x-2"
        onClick={() => {
          if (
            summary?.pelaksanaanStatuses?.praktikKinerja === 'SELESAI' &&
            formName === 'diskusi_persiapan'
          ) {
            setWarningDialogAppealRollbackOpen(true)
            return
          }
          setDialogAppealRollbackOpen(true)
        }}
        size="md"
        variant="solid"
        disabled={isDisabled}
      >
        <Icon fontSize="sm" color={isDisabled ? 'disabled' : 'default'}>
          lock_outline
        </Icon>
        <Text color={isDisabled ? 'disabled' : 'primary'}>
          Ubah isi {PELAKSANAAN_PRAKTIK_KINERJA_STATE_TITLE?.[formName]}
        </Text>
      </Button>

      {isWarningDialogAppealRollbackOpen && (
        <DialogWarningPelaksanaanAppealRollback
          isOpen
          isLoading={false}
          onClose={() => {
            setWarningDialogAppealRollbackOpen(false)
          }}
          onConfirm={() => {
            setWarningDialogAppealRollbackOpen(false)
            setTimeout(() => {
              setDialogAppealRollbackOpen(true)
            }, 50)
          }}
        />
      )}

      {isNeedRollbackApproval ? (
        <DialogPelaksanaanAppealRequest
          formName={formName}
          currentState={summary?.pelaksanaanStatuses?.praktikKinerja}
          isLoading={false}
          isOpen={isDialogAppealRollbackOpen}
          onClose={() => {
            setDialogAppealRollbackOpen(false)
          }}
          onConfirm={() => {
            handleRollback()
          }}
        />
      ) : (
        <DialogPelaksanaanAppealRollback
          isLoading={false}
          isOpen={isDialogAppealRollbackOpen}
          onClose={() => {
            setDialogAppealRollbackOpen(false)
          }}
          onConfirm={() => {
            handleRollback()
          }}
        />
      )}

      <DialogPelaksanaanAppealSubmitted
        isOpen={isDialogAppealSubmittedOpen}
        onClose={async () => {
          setDialogAppealSubmittedOpen(false)
          await router.push(
            `${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`
          )
          toast({ message: MSG_SUCCESS_REQUEST, type: 'success' })
        }}
      />
    </>
  )
}
